import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { AuthGate } from "@/components/AuthGate";
import { PageContainer } from "@/components/PageContainer";
import { Eyebrow, DisplayHeading } from "@/components/editorial";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { getStudentLibrary } from "@/lib/library.functions";

export const Route = createFileRoute("/library")({
  head: () => ({
    meta: [
      { title: "Library — Tiger's Eye" },
      { name: "description", content: "Browse the Tiger's Eye knowledge base, university policies and your marker feedback." },
      { name: "robots", content: "noindex,follow" },
      { property: "og:title", content: "Library — Tiger's Eye" },
      { property: "og:description", content: "Knowledge base, university policies and your feedback in one place." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: () => (<AuthGate><LibraryPage /></AuthGate>),
});

const fmt = (d: string) => new Date(d).toLocaleDateString("en-GB");

function LibraryPage() {
  const fetchLib = useServerFn(getStudentLibrary);
  const { data, isLoading, error } = useQuery({ queryKey: ["student-library"], queryFn: () => fetchLib() });
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();
  const match = (...s: (string | null | undefined)[]) => !term || s.some((x) => x?.toLowerCase().includes(term));

  const kg = useMemo(() => (data?.knowledge ?? []).filter((n) => match(n.title, n.body, n.type)), [data, term]);
  const pol = useMemo(() => (data?.policies ?? []).filter((p) => match(p.institution, p.summary)), [data, term]);
  const fb = useMemo(() => (data?.feedback ?? []).filter((f) => match(f.marker_comment, f.final_grade)), [data, term]);

  return (
    <PageContainer>
      <Eyebrow className="mb-3">Library</Eyebrow>
      <DisplayHeading level={1} className="mb-6">Knowledge, policies and feedback</DisplayHeading>
      <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="mb-6 max-w-md" />

      {isLoading && <p className="text-ink-secondary">Loading…</p>}
      {error && <p role="alert" className="text-destructive">{(error as Error).message}</p>}

      {data && (
        <Tabs defaultValue="kb">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="kb">Knowledge base ({kg.length})</TabsTrigger>
            <TabsTrigger value="policies">University policies ({pol.length})</TabsTrigger>
            <TabsTrigger value="feedback">My feedback ({fb.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="kb" className="space-y-3 pt-4">
            {kg.length === 0 && <p className="text-ink-secondary">No entries found.</p>}
            {kg.map((n) => (
              <details key={n.id} className="border border-border bg-card p-4">
                <summary className="cursor-pointer font-serif text-lg text-primary break-words">
                  {n.title} <span className="text-xs text-ink-tertiary uppercase ml-2">{n.type}</span>
                </summary>
                <p className="mt-3 whitespace-pre-wrap text-sm text-ink-primary break-words">{n.body}</p>
                {n.source_uri && (
                  <a href={n.source_uri} target="_blank" rel="noreferrer" className="mt-2 inline-block text-sm text-primary underline">Source</a>
                )}
              </details>
            ))}
          </TabsContent>

          <TabsContent value="policies" className="space-y-3 pt-4">
            {pol.length === 0 && <p className="text-ink-secondary">No policies found.</p>}
            {pol.map((p) => (
              <div key={p.id} className="border border-border bg-card p-4">
                <h2 className="font-serif text-lg text-primary">{p.institution}</h2>
                <p className="text-xs text-ink-tertiary mb-2">Updated {fmt(p.last_scraped)}</p>
                {p.summary && <p className="text-sm whitespace-pre-wrap break-words">{p.summary}</p>}
                {p.model_disclosure_text && (
                  <p className="mt-3 text-sm italic border-l-2 border-border pl-3 break-words">{p.model_disclosure_text}</p>
                )}
                {p.source_url && (
                  <a href={p.source_url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-sm text-primary underline">Read the full policy</a>
                )}
              </div>
            ))}
          </TabsContent>

          <TabsContent value="feedback" className="space-y-3 pt-4">
            {fb.length === 0 && <p className="text-ink-secondary">You haven't added any marker feedback yet.</p>}
            {fb.map((f) => (
              <div key={f.id} className="border border-border bg-card p-4">
                <div className="flex flex-wrap justify-between gap-2 text-xs text-ink-tertiary mb-2">
                  <span>{fmt(f.created_at)}{f.final_grade ? ` · Grade: ${f.final_grade}` : ""}</span>
                  <Link to="/reviews/$reviewId" params={{ reviewId: f.review_id }} className="text-primary underline">View review</Link>
                </div>
                <p className="text-sm whitespace-pre-wrap break-words">{f.marker_comment}</p>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      )}

      <div className="mt-10 border border-border bg-card p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-serif text-lg text-primary">Free while founding places last</h2>
            <p className="mt-1 text-sm text-ink-secondary">
              Tiger's Eye is free for the first 49 students, then &pound;4.95 per term.
            </p>
          </div>
          <Link
            to="/pricing"
            className="inline-flex shrink-0 items-center rounded-full border border-border px-5 py-2 text-sm text-primary hover:underline"
          >
            See pricing
          </Link>
        </div>
      </div>
    </PageContainer>
  );
}
